import { useCallback } from 'react';

import { ASSET_TYPES, ENTITY_TYPES, NODE_TYPES } from '@/shared/api/openmetadata';
import type { AssetTreeNode } from '@/shared/lib/tree';
import { Icon } from '@/shared/ui/Icon';
import type { AssetType } from '@/shared/ui/Icon/Icon';
import { ServiceIcon } from '@/shared/ui/ServiceIcon';

const EXCLUDED_NODE_KEYS = ['governance-metric'];
const ICON_SIZE = 14;

const EXPERIMENT_ASSET_TYPES = {
  PROJECT: 'project',
  EXPERIMENT: 'experiment',
  RUN: 'run',
  FEATURE_SET: 'feature_set',
  DATASET: 'dataset',
  PARAMETER_SET: 'parameter_set',
  ARTIFACT: 'artifact',
} as const;

const NODE_ICON_MAP: Partial<Record<string, AssetType>> = {
  [NODE_TYPES.DATABASE]: ASSET_TYPES.DATABASE,
  [NODE_TYPES.SCHEMA]: ASSET_TYPES.DATABASE_SCHEMA,
  [NODE_TYPES.TOPIC]: ASSET_TYPES.TOPIC,
  [NODE_TYPES.TABLE]: ASSET_TYPES.TABLE,
};

const EXPERIMENT_ICON_MAP: Record<string, AssetType> = {
  [EXPERIMENT_ASSET_TYPES.PROJECT]: ASSET_TYPES.CONTAINER,
  [EXPERIMENT_ASSET_TYPES.EXPERIMENT]: ASSET_TYPES.EXPERIMENT,
  [EXPERIMENT_ASSET_TYPES.RUN]: ASSET_TYPES.RUN,
  [EXPERIMENT_ASSET_TYPES.FEATURE_SET]: ASSET_TYPES.FEATURE_SET,
  [EXPERIMENT_ASSET_TYPES.DATASET]: ASSET_TYPES.DATASET,
  [EXPERIMENT_ASSET_TYPES.PARAMETER_SET]: ASSET_TYPES.PARAMETER_SET,
  [EXPERIMENT_ASSET_TYPES.ARTIFACT]: ASSET_TYPES.ARTIFACT,
};

type UseAssetTreeRenderResult = {
  renderTitle: (node: AssetTreeNode, styles: Record<string, string>) => React.ReactNode;
  enrichTreeData: (nodes: AssetTreeNode[], styles: Record<string, string>) => AssetTreeNode[];
};

export const useAssetTreeRender = (): UseAssetTreeRenderResult => {
  const renderIcon = useCallback((node: AssetTreeNode) => {
    if (EXPERIMENT_ICON_MAP[node.type]) {
      return <Icon assetType={EXPERIMENT_ICON_MAP[node.type]} size={ICON_SIZE} color="#000000" />;
    }

    if (node.type === NODE_TYPES.SERVICE) {
      const nodeData = node.data as { parentEntityType?: string };
      const shouldUseServiceIcon =
        node.serviceType &&
        (nodeData.parentEntityType === ENTITY_TYPES.MLMODEL ||
          nodeData.parentEntityType === ENTITY_TYPES.CONTAINER ||
          nodeData.parentEntityType === ENTITY_TYPES.DATABASE);

      if (shouldUseServiceIcon && node.serviceType) {
        return <ServiceIcon serviceType={node.serviceType} size={ICON_SIZE} />;
      }
      return <Icon assetType={ASSET_TYPES.DATABASE} size={ICON_SIZE} />;
    }

    if (node.type === NODE_TYPES.SERVICE_TYPE && node.serviceType) {
      return <ServiceIcon serviceType={node.serviceType} size={ICON_SIZE} />;
    }

    if (NODE_ICON_MAP[node.type]) {
      return <Icon assetType={NODE_ICON_MAP[node.type]!} size={ICON_SIZE} />;
    }

    const fallbackType =
      node.type === NODE_TYPES.ENTITY_TYPE
        ? (node.serviceType as AssetType) || ASSET_TYPES.DATABASE
        : ASSET_TYPES.DATABASE;

    return <Icon assetType={fallbackType} size={ICON_SIZE} />;
  }, []);

  const extractLabelAndCount = useCallback((title: string | React.ReactNode): { label: string; count?: string } => {
    if (typeof title !== 'string') {
      return { label: String(title) };
    }

    const match = title.match(/^(.+?)\s+(\d+)$/);
    if (match) {
      return { label: match[1].trim(), count: match[2] };
    }

    return { label: title };
  }, []);

  const renderTitle = useCallback(
    (node: AssetTreeNode, styles: Record<string, string>) => {
      const { label } = extractLabelAndCount(node.title);

      const nodeData = node.data as { count?: number; entityType?: string } | undefined;
      const isExperiment = node.type === 'experiment' || nodeData?.entityType === 'experiment';
      const shouldShowCount = isExperiment && nodeData?.count != null;
      const count = shouldShowCount ? nodeData.count : undefined;

      return (
        <span className={styles.treeNodeTitle}>
          <span className={styles.nodeIconLabel}>
            {renderIcon(node)}
            <span className={styles.nodeLabel}>{label}</span>
          </span>
          {count !== undefined && <span className={styles.nodeCount}>{count}</span>}
        </span>
      );
    },
    [renderIcon, extractLabelAndCount]
  );

  const enrichTreeData = useCallback(
    (nodes: AssetTreeNode[], styles: Record<string, string>): AssetTreeNode[] => {
      return nodes
        .filter((node) => !EXCLUDED_NODE_KEYS.includes(node.key))
        .map((node) => ({
          ...node,
          title: renderTitle(node, styles),
          disabled: node.disabled || false,
          selectable: !node.disabled,
          children: node.children ? enrichTreeData(node.children, styles) : undefined,
        }));
    },
    [renderTitle]
  );

  return {
    renderTitle,
    enrichTreeData,
  };
};
